import axios from 'axios';
import { debounce } from 'lodash-es';
import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';

interface UsePdfPreviewParams {
	/** Generates the PDF blob for the current form state. */
	generate: (signal: AbortSignal) => Promise<Blob>;
	/** Returns the file name used for the generated preview object. */
	getFileName: () => string;
	/** Debounce in ms between the last form change and preview generation. */
	previewDebounce?: number;
	/** Called when generation fails (non-cancel error). */
	onError?: () => void;
	disabled?: boolean;
}

/**
 * Generic, debounced PDF preview generator shared by PDF print dialogs.
 * Encapsulates the abort-on-change, blob -> object URL and error handling
 * logic. The feature provides the actual `generate` call.
 */
export const usePdfPreview = ({
	generate,
	getFileName,
	previewDebounce = 1500,
	onError,
	disabled,
}: UsePdfPreviewParams) => {
	const { t } = useTranslation('common');
	const { enqueueSnackbar } = useSnackbar();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();

	const [previewUrl, setPreviewUrl] = useState('');
	const [isLoadingPreview, setIsLoadingPreview] = useState(false);

	const controller = useRef(new AbortController());

	// Keep latest callbacks in refs so the debounced function stays stable.
	const generateRef = useRef(generate);
	generateRef.current = generate;
	const getFileNameRef = useRef(getFileName);
	getFileNameRef.current = getFileName;
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	const isDisabled = useRef(disabled);
	isDisabled.current = disabled;

	const generatePreviewRef = useRef(() => {
		if (isDisabled.current) {
			return;
		}

		setIsLoadingPreview(true);

		const cancelSignal = new AbortController();
		controller.current = cancelSignal;

		generateRef
			.current(cancelSignal.signal)
			.then((blob) => {
				const pdfFile = new File([blob], getFileNameRef.current(), {
					type: 'application/pdf',
				});

				setPreviewUrl(URL.createObjectURL(pdfFile));
				setIsLoadingPreview(false);
			})
			.catch((error) => {
				// after cancel we still show the spinner, so the user won't see
				// any stale state
				if (!axios.isCancel(error)) {
					if (checkConsoleDebuggingReadAccess()) {
						console.error('Error generating PDF:', error);
					}

					enqueueSnackbar(t('error-unexpected'), {
						variant: 'error',
					});

					setIsLoadingPreview(false);
					onErrorRef.current?.();
				}
			});
	});

	// debounced reference must be stable between renders
	const debouncedGenerate = useRef(
		debounce(() => {
			generatePreviewRef.current();
		}, previewDebounce)
	);

	const triggerPreview = useCallback(() => {
		// always immediately abort the previous request if it is still running
		if (controller.current != null) {
			controller.current.abort();
		}
		debouncedGenerate.current();
	}, []);

	return { previewUrl, isLoadingPreview, triggerPreview };
};
